import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AlertWithTimediff } from 'src/app/models/Alert';
import { ClientPortalUser, IClientPortalUser } from 'src/app/models/Client';
import { ElectricityPrice } from 'src/app/models/EnergyPrice';
import { ServerResponse } from 'src/app/models/ServerResponse';
import { AlertsService } from 'src/app/services/alerts.service';
import { ElectricityPricesService } from 'src/app/services/electricity-prices.service';
import { UsersService } from 'src/app/services/users.service';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {

    // The alerts subject lives in a root service, so this would otherwise keep every visited dashboard alive
    private _alertsSubscription?: Subscription;

    public averageElectricityQuantity: number = 0; // Average electricity price in EUR/MWh
    public averageGasQuantity: number = 31.73; // Average gas price in EUR/MWh (hardcoded for now)
    public chartOptions: any; // Chart options for the electricity prices chart

    public selectedDate = new Date();
    public minDate!: Date;
    public maxDate!: Date;

    public _isLoadingFlag = false; // Loading flag for the component

    public user?: ClientPortalUser; // User data from the user service
    public userAlerts: AlertWithTimediff[] = []; // User alerts from the alerts service

    constructor(private _usersService: UsersService, protected _alertsService: AlertsService, private _electricityPricesService: ElectricityPricesService,
        private _translateService: TranslateService,
        private _router: Router
    ) {
        this.user = _usersService.userData ?? undefined;
    }

    /**
     * OnInit lifecycle hook. This method is called when the component is initialized.
     * It fetches the market energy prices for today until mid day otherwise fetches tomorrow prices and subscribes to user alerts.
     * @returns {void}
     */
    ngOnInit(): void {
        this.setSelectableDates();
        this.onSelectedDateChange();

        //Get alerts
        this._alertsSubscription = this._alertsService.allUserAlerts$.subscribe((alerts: AlertWithTimediff[]) => {
            this.userAlerts = alerts;
        })
    }

    ngOnDestroy(): void {
        this._alertsSubscription?.unsubscribe();
    }

    /**
     * 
     * Get the market electricity prices by date range. The data is fetched from the Entsoe service and the chart is updated with the new data.
     * @param {Date} start Start date of the range
     * @param {Date} end End date of the range
     * @returns {void}
     */
    private getMarketEnergyPricesByDate(start: Date, end: Date): void {
        this._electricityPricesService.getMarketElectricityPricesByDate(start, end).subscribe((response: ServerResponse<ElectricityPrice[]>) => {
            const prices = response.data;

            // Calculate average electricity price in EUR/MWh
            this.averageElectricityQuantity = prices.length > 0
                ? +(prices.reduce((sum: number, p: any) => sum + p.priceEURPerMWh, 0) / prices.length).toFixed(2)
                : 0;

            const categories = prices.map((p: any) => p.period);
            const seriesData = prices.map((p: any) => p.priceEURPerMWh);

            // Update the chart options with the new data
            this.chartOptions = {
                series: [
                    {
                        name: this._translateService.instant('PRICE') + ' (€/MWh)',
                        data: seriesData
                    }
                ],
                chart: {
                    type: 'bar',
                    height: 350,
                    toolbar: {
                        show: false
                    }
                },
                plotOptions: {
                    bar: {
                        borderRadius: 8,
                        borderRadiusApplication: "end",
                        columnWidth: '50%',
                        dataLabels: {
                            position: 'top'
                        }
                    }
                },
                dataLabels: {
                    enabled: false
                },
                colors: ['#addc3a'],
                xaxis: {
                    categories: categories,
                    title: {
                        text: this._translateService.instant('PERIOD')
                    }
                },
                yaxis: {
                    title: {
                        text: 'EUR/MWh'
                    }
                },
                tooltip: {
                    x: {
                        formatter: (val: string) => {
                            return `${this._translateService.instant('PERIOD')}: ${val}`;
                        }
                    }
                }
            };
        });
    }

    /**
     * Date range change event handler. When the date range is changed, the market energy prices are fetched for the new date range.
     * @returns {void}
     */
    protected onSelectedDateChange(): void {
        if (this.selectedDate) {
            this.getMarketEnergyPricesByDate(this.selectedDate, this.selectedDate);
        }
    }

    /**
     * Set the current date and sets the min and max dates available on datepicker
     * @returns {void}
     */
    private setSelectableDates(): void {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);

        const lastYear = new Date(today);
        lastYear.setFullYear(today.getFullYear() - 1);

        const isBeforeNoon = now.getHours() < 12;

        this.minDate = lastYear;
        this.maxDate = isBeforeNoon ? today : tomorrow;
        this.selectedDate = isBeforeNoon ? today : tomorrow;
    }
    
    navigateTo(url: string) {
        this._router.navigate([url])
    }
}


